import clsx from 'clsx';
import {missingClass} from '~/lib/utils';

export type IconProps = JSX.IntrinsicElements['svg'] & {
  direction?: 'up' | 'right' | 'down' | 'left';
};

function Icon({
  children,
  className,
  fill = 'currentColor',
  stroke,
  ...props
}: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      {...props}
      fill={fill}
      stroke={stroke}
      className={clsx(missingClass(className, 'w-') && 'h-5 w-5', className)}
    >
      {children}
    </svg>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <svg
      {...props}
      width="24"
      height="10"
      viewBox="0 0 24 10"
      xmlns="http://www.w3.org/2000/svg"
      fill=""
    >
      <title>Menu</title>
      <rect y="8" width="24" height="2" fill={props.fill || 'currentColor'} />
      <rect y="0" width="24" height="2" fill={props.fill || 'currentColor'} />
    </svg>
  );
}

export function IconClose(props: IconProps) {
  return (
    <Icon {...props} stroke={props.stroke || 'currentColor'}>
      <title>Close</title>
      <line
        x1="4.44194"
        y1="4.30806"
        x2="15.7556"
        y2="15.6218"
        strokeWidth="1.25"
      />
      <line
        y1="-0.625"
        x2="16"
        y2="-0.625"
        transform="matrix(-0.707107 0.707107 0.707107 0.707107 16 4.75)"
        strokeWidth="1.25"
      />
    </Icon>
  );
}

export function IconArrow({direction = 'right'}: IconProps) {
  let rotate;

  switch (direction) {
    case 'right':
      rotate = 'rotate-0';
      break;
    case 'left':
      rotate = 'rotate-180';
      break;
    case 'up':
      rotate = '-rotate-90';
      break;
    case 'down':
      rotate = 'rotate-90';
      break;
    default:
      rotate = 'rotate-0';
  }

  return (
    <Icon className={`h-5 w-5 ${rotate}`}>
      <title>Arrow</title>
      <path d="M7 3L14 10L7 17" strokeWidth="1.25" />
    </Icon>
  );
}

export function IconCaret({
  direction = 'down',
  stroke = 'currentColor',
  ...props
}: IconProps) {
  let rotate;

  switch (direction) {
    case 'down':
      rotate = 'rotate-0';
      break;
    case 'up':
      rotate = 'rotate-180';
      break;
    case 'left':
      rotate = '-rotate-90';
      break;
    case 'right':
      rotate = 'rotate-90';
      break;
    default:
      rotate = 'rotate-0';
  }

  return (
    <Icon
      {...props}
      className={`h-5 w-5 transition ${rotate}`}
      fill="transparent"
      stroke={stroke}
    >
      <title>Caret</title>
      <path d="M14 8L10 12L6 8" strokeWidth="1.25" />
    </Icon>
  );
}

export function IconSelect(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Select</title>
      <path d="M7 8.5L10 6.5L13 8.5" strokeWidth="1.25" />
      <path d="M13 11.5L10 13.5L7 11.5" strokeWidth="1.25" />
    </Icon>
  );
}

export function IconBag(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Bag</title>
      <path
        fillRule="evenodd"
        d="M8.125 5a1.875 1.875 0 0 1 3.75 0v.375h-3.75V5Zm-1.25.375V5a3.125 3.125 0 1 1 6.25 0v.375h3.5V15A2.625 2.625 0 0 1 14 17.625H6A2.625 2.625 0 0 1 3.375 15V5.375h3.5ZM4.625 15V6.625h10.75V15c0 .76-.616 1.375-1.375 1.375H6c-.76 0-1.375-.616-1.375-1.375Z"
      />
    </Icon>
  );
}

export function IconAccount(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Account</title>
      <path
        fillRule="evenodd"
        d="M9.9998 12.625c-1.9141 0-3.6628.698-5.0435 1.8611C3.895 13.2935 3.25 11.7221 3.25 10c0-3.728 3.022-6.75 6.75-6.75 3.7279 0 6.75 3.022 6.75 6.75 0 1.7222-.645 3.2937-1.7065 4.4863-1.3807-1.1632-3.1295-1.8613-5.0437-1.8613ZM10 18c-2.3556 0-4.4734-1.0181-5.9374-2.6382C2.7806 13.9431 2 12.0627 2 10c0-4.4183 3.5817-8 8-8s8 3.5817 8 8-3.5817 8-8 8Zm0-12.5c-1.567 0-2.75 1.394-2.75 3s1.183 3 2.75 3 2.75-1.394 2.75-3-1.183-3-2.75-3Z"
      />
    </Icon>
  );
}

export function IconLogin(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Login</title>
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <path
          d="M8,10.6928545 C10.362615,10.6928545 12.4860225,11.7170237 13.9504747,13.3456144 C12.4860225,14.9758308 10.362615,16 8,16 C5.63738499,16 3.51397752,14.9758308 2.04952533,13.3472401 C3.51397752,11.7170237 5.63738499,10.6928545 8,10.6928545 Z"
          fill="currentColor"
        ></path>
        <path
          d="M8,3.5 C6.433,3.5 5.25,4.894 5.25,6.5 C5.25,8.106 6.433,9.5 8,9.5 C9.567,9.5 10.75,8.106 10.75,6.5 C10.75,4.894 9.567,3.5 8,3.5 Z"
          fill="currentColor"
          fillRule="nonzero"
        ></path>
      </g>
    </Icon>
  );
}

export function IconHelp(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Help</title>
      <path d="M3.375 10a6.625 6.625 0 1 1 13.25 0 6.625 6.625 0 0 1-13.25 0ZM10 2.125a7.875 7.875 0 1 0 0 15.75 7.875 7.875 0 0 0 0-15.75Zm.699 10.507H9.236V14h1.463v-1.368ZM7.675 7.576A3.256 3.256 0 0 0 7.5 8.67h1.245c0-.496.105-.89.316-1.182.218-.299.553-.448 1.005-.448a1 1 0 0 1 .327.065c.124.044.24.113.35.208.108.095.2.223.272.383.08.154.12.34.12.558a1.3 1.3 0 0 1-.076.471c-.044.131-.11.252-.197.361-.08.102-.174.197-.283.285-.102.087-.212.182-.328.284a3.157 3.157 0 0 0-.382.383c-.102.124-.19.27-.262.438a2.476 2.476 0 0 0-.164.591 6.333 6.333 0 0 0-.043.81h1.179c0-.263.021-.485.065-.668a1.65 1.65 0 0 1 .207-.47c.088-.139.19-.263.306-.372.117-.11.244-.223.382-.34l.35-.306c.116-.11.218-.23.305-.361.095-.139.168-.3.219-.482.058-.19.087-.412.087-.667 0-.35-.062-.664-.186-.942a1.881 1.881 0 0 0-.513-.689 2.07 2.07 0 0 0-.753-.427A2.721 2.721 0 0 0 10.12 6c-.4 0-.764.066-1.092.197a2.36 2.36 0 0 0-.83.536c-.225.234-.4.515-.523.843Z" />
    </Icon>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <Icon {...props}>
      <title>Search</title>
      <path
        d="M9.16667 14.5833C12.1582 14.5833 14.5833 12.1582 14.5833 9.16667C14.5833 6.17512 12.1582 3.75 9.16667 3.75C6.17512 3.75 3.75 6.17512 3.75 9.16667C3.75 12.1582 6.17512 14.5833 9.16667 14.5833Z"
        strokeWidth="1.5"
        stroke="currentColor"
        fill="none"
      />
      <path
        d="M17 17.0834L12.9167 13.0834"
        strokeWidth="1.5"
        stroke="currentColor"
      />
    </Icon>
  );
}

export function IconCheck({
  stroke = 'currentColor',
  ...props
}: React.ComponentProps<typeof Icon>) {
  return (
    <Icon {...props} fill="transparent" stroke={stroke}>
      <title>Check</title>
      <circle cx="10" cy="10" r="7.25" strokeWidth="1.25" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="m7.04 10.37 2.42 2.41 3.5-5.56"
      />
    </Icon>
  );
}

export function IconXMark({
  stroke = 'currentColor',
  ...props
}: React.ComponentProps<typeof Icon>) {
  return (
    <Icon {...props} fill="transparent" stroke={stroke}>
      <title>Delete</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </Icon>
  );
}

export function IconRemove(props: IconProps) {
  return (
    <Icon {...props} fill="transparent" stroke={props.stroke || 'currentColor'}>
      <title>Remove</title>
      <path
        d="M4 6H16"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8.5 9V14" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.5 9V14" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M5.5 6L6 17H14L14.5 6"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 6L8 5C8 4 8.75 3 10 3C11.25 3 12 4 12 5V6"
        strokeWidth="1.25"
      />
    </Icon>
  );
}

export function IconFilters(props: IconProps) {
  return (
    <Icon {...props} fill="transparent" stroke={props.stroke || 'currentColor'}>
      <title>Filters</title>
      <circle cx="4.5" cy="6.5" r="2" />
      <line x1="6" y1="6.5" x2="14" y2="6.5" />
      <line x1="4.37114e-08" y1="6.5" x2="3" y2="6.5" />
      <line x1="4.37114e-08" y1="13.5" x2="8" y2="13.5" />
      <line x1="11" y1="13.5" x2="14" y2="13.5" />
      <circle cx="9.5" cy="13.5" r="2" />
    </Icon>
  );
}

export function IconArrowCircleRight(props: IconProps) {
  return (
    <Icon
      {...props}
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMinYMin meet"
    >
      <circle
        r="50%"
        cx="50%"
        cy="50%"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeMiterlimit="10"
      />
      <path
        d="M13.75 21.25L19 16L13.75 10.75"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeMiterlimit="10"
      />
    </Icon>
  );
}

export function IconMadeInBritain(props: IconProps) {
  return (
    <Icon
      {...props}
      fill="currentColor"
      stroke="currentColor"
      width="229"
      height="19"
      viewBox="0 0 229 19"
    >
      <path d="M0.385805 19V0.0999987H4.48981L10.5108 12.088L16.4508 0.0999987H20.5548V19H17.0988V5.959L11.8338 16.3H9.10681L3.8418 5.959V19H0.385805ZM24.0067 19L30.9187 0.0999987H34.8067L41.7187 19H38.0467L36.5347 14.626H29.1637L27.6247 19H24.0067ZM30.1087 11.926H35.5897L32.8357 4.069L30.1087 11.926ZM45.1534 19V0.0999987H51.6064C53.8204 0.0999987 55.6384 0.495999 57.0604 1.288C58.5004 2.062 59.5624 3.16 60.2464 4.582C60.9484 5.986 61.2994 7.642 61.2994 9.55C61.2994 11.458 60.9484 13.123 60.2464 14.545C59.5624 15.949 58.5004 17.047 57.0604 17.839C55.6384 18.613 53.8204 19 51.6064 19H45.1534ZM48.6094 16.03H51.4444C53.0284 16.03 54.2794 15.778 55.1974 15.274C56.1154 14.752 56.7724 14.014 57.1684 13.06C57.5644 12.088 57.7624 10.918 57.7624 9.55C57.7624 8.2 57.5644 7.039 57.1684 6.067C56.7724 5.095 56.1154 4.348 55.1974 3.826C54.2794 3.304 53.0284 3.043 51.4444 3.043H48.6094V16.03ZM65.3487 19V0.0999987H77.6877V2.881H68.8047V8.065H76.8777V10.765H68.8047V16.219H77.6877V19H65.3487ZM89.3917 19V0.0999987H92.8477V19H89.3917ZM97.5108 19V0.0999987H100.967L109.85 13.411V0.0999987H113.306V19H109.85L100.967 5.716V19H97.5108ZM125.403 19V0.0999987H132.99C134.952 0.0999987 136.446 0.558999 137.472 1.477C138.516 2.377 139.038 3.538 139.038 4.96C139.038 6.148 138.714 7.102 138.066 7.822C137.436 8.524 136.662 9.001 135.744 9.253C136.824 9.469 137.715 10.009 138.417 10.873C139.119 11.719 139.47 12.709 139.47 13.843C139.47 15.337 138.93 16.57 137.85 17.542C136.77 18.514 135.24 19 133.26 19H125.403ZM128.859 8.038H132.477C133.449 8.038 134.196 7.813 134.718 7.363C135.24 6.913 135.501 6.274 135.501 5.446C135.501 4.654 135.24 4.033 134.718 3.583C134.214 3.115 133.449 2.881 132.423 2.881H128.859V8.038ZM128.859 16.192H132.72C133.746 16.192 134.538 15.958 135.096 15.49C135.672 15.004 135.96 14.329 135.96 13.465C135.96 12.583 135.663 11.89 135.069 11.386C134.475 10.882 133.674 10.63 132.666 10.63H128.859V16.192ZM143.437 19V0.0999987H150.376C151.888 0.0999987 153.13 0.360999 154.102 0.883C155.092 1.387 155.83 2.08 156.316 2.962C156.802 3.826 157.045 4.789 157.045 5.851C157.045 7.003 156.739 8.056 156.127 9.01C155.533 9.964 154.597 10.657 153.319 11.089L157.234 19H153.265L149.755 11.548H146.893V19H143.437ZM146.893 9.01H150.16C151.312 9.01 152.158 8.731 152.698 8.173C153.238 7.615 153.508 6.877 153.508 5.959C153.508 5.059 153.238 4.339 152.698 3.799C152.176 3.259 151.321 2.989 150.133 2.989H146.893V9.01ZM161.259 19V0.0999987H164.715V19H161.259ZM173.779 19V2.881H168.271V0.0999987H182.77V2.881H177.235V19H173.779ZM183.111 19L190.023 0.0999987H193.911L200.823 19H197.151L195.639 14.626H188.268L186.729 19H183.111ZM189.213 11.926H194.694L191.94 4.069L189.213 11.926ZM204.258 19V0.0999987H207.714V19H204.258ZM212.377 19V0.0999987H215.833L224.716 13.411V0.0999987H228.172V19H224.716L215.833 5.716V19H212.377Z" />
    </Icon>
  );
}
